import { SearchState, Filter as SearchkitFilter } from '@searchkit/client';
import { isEqual, keyBy } from 'lodash';
import { search } from 'sr-types';
import RenderFilterPreview from '../../common/components/RenderFilterPreview';
import { FILTER_IDENTIFIERS, IDENTIFIER } from '../../common/constants/constants';
import {
    Filter,
    SAVED_FILTERS_CONSTANTS,
    SavedFilterItem,
    type SelectedSavedFilter
} from '../../common/types/savedFilters';
import type { SavePreferenceConfig } from '../../common/types/savePreference';
import { toDisplay } from '../../common/utils/dateTime';

type SearchkitApi = {
    searchState: SearchState;
    setSearchState: (state: SearchState) => void;
    search: () => void;
};

/**
 * Builds a stable string key for a filter combining its identifier and value.
 */
export const buildFilterKey = (f: Filter): string => {
    if (f.dateMin || f.dateMax) {
        return `${f.identifier}__${f.dateMin ?? ''}__${f.dateMax ?? ''}`;
    }
    return `${f.identifier}__${String(f.value ?? '')}`;
};

/**
 * Normalizes filters by mapping to keys, and sorting.
 */
export const normalizeFilters = (filters: Filter[]): string[] => {
    return (filters || []).map((f) => buildFilterKey(f)).sort();
};

/**
 * Builds the base filter set from immutable, required, and the special 'entity' filter.
 */
export const composeBaseFilters = (
    currentState: SearchState,
    requiredFilters: string[] = [],
    immutableFilters: Filter[] = []
): Filter[] => {
    const base: Filter[] = [];
    if (Array.isArray(immutableFilters) && immutableFilters.length) base.push(...immutableFilters);

    const filtersArray: Filter[] = Array.isArray(currentState?.filters) ? (currentState.filters as Filter[]) : [];
    if (Array.isArray(requiredFilters) && requiredFilters.length && filtersArray.length) {
        const byIdentifier = keyBy(filtersArray, IDENTIFIER);
        requiredFilters.forEach((id) => {
            const found = byIdentifier[id];
            if (found) base.push(found);
        });
    }

    if (filtersArray.length) {
        const entityFilter = filtersArray.find((f: Filter) => f.identifier === FILTER_IDENTIFIERS.entity);
        if (entityFilter) base.push(entityFilter as Filter);
    }
    return base;
};

/**
 * Reads the selected saved filter from localStorage by key.
 */
export const readSelectedSavedFilter = (key: string): SelectedSavedFilter | null => {
    try {
        const raw = window.localStorage.getItem(key);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

/**
 * Persists the selected saved filter to localStorage under the given key.
 */
export const writeSelectedSavedFilter = (key: string, payload: SelectedSavedFilter) => {
    window.localStorage.setItem(key, JSON.stringify(payload));
};

/**
 * Removes the selected saved filter entry from localStorage.
 */
export const clearSelectedSavedFilter = (key: string) => {
    window.localStorage.removeItem(key);
};

/**
 * Converts custom Filter type to SearchkitFilter type.
 */
const convertToSearchkitFilters = (filters: Filter[]): SearchkitFilter[] => {
    return filters.map((filter): SearchkitFilter & { dateMin?: string; dateMax?: string } => {
        if (filter.dateMin || filter.dateMax) {
            return {
                identifier: filter.identifier,
                dateMin: filter.dateMin,
                dateMax: filter.dateMax
            };
        }
        return {
            identifier: filter.identifier,
            value: String(filter.value ?? '')
        };
    });
};

/**
 * Applies search state with new filters and executes search.
 */
export const applySearchState = (api: SearchkitApi, currentState: SearchState, newFilters: Filter[]) => {
    api.setSearchState({
        ...currentState,
        filters: convertToSearchkitFilters(newFilters),
        page: { from: 0, size: currentState?.page?.size || 25 }
    });
    api.search();
};

/**
 * Trims form data.
 */
export const processFilterData = (currentFilters: Filter[], formData: { name: string; description: string }) => {
    const name = formData.name?.trim();
    const description = formData.description?.trim() || '';
    return { filtered: currentFilters, name, description };
};

export const getDisplayFilters = (appliedFilters: Filter[], excludedIdentifiers: string[] = []) => {
    const excluded = new Set([...SAVED_FILTERS_CONSTANTS.EXCLUDED_FILTERS, ...(excludedIdentifiers || [])]);

    return appliedFilters.filter((filter) => !excluded.has(filter.identifier));
};

export const getGroupedFilters = (displayFilters: Filter[], locale: string) =>
    displayFilters.reduce(
        (acc, filter) => {
            if (!acc[filter.identifier]) acc[filter.identifier] = [];
            if (filter.dateMin || filter.dateMax) {
                const min = filter.dateMin ? toDisplay(filter.dateMin, false, locale) : '';
                const max = filter.dateMax ? toDisplay(filter.dateMax, false, locale) : '';
                acc[filter.identifier].push(min && max ? `${min} - ${max}` : min || max);
            } else {
                acc[filter.identifier].push(String(filter.value ?? ''));
            }
            return acc;
        },
        {} as Record<string, string[]>
    );

export const toLabel = (identifier: string, facets: search.SkFacetSet[]) => {
    const facet = facets?.find((f) => f.identifier === identifier);
    return facet?.label;
};

export const findMatchingSavedFilter = (
    savedFilters: SavedFilterItem[],
    normalizedCurrentFilters: string[],
    excludedIdentifiers: string[] = []
): SavedFilterItem | null => {
    if (!savedFilters.length || normalizedCurrentFilters.length === 0) return null;
    return (
        savedFilters.find((savedFilter) => {
            const normalizedSavedFilters = normalizeFilters(
                getDisplayFilters(savedFilter.filters, excludedIdentifiers)
            );
            return isEqual(normalizedCurrentFilters, normalizedSavedFilters);
        }) || null
    );
};

export const createSavedFilterConfig = (
    groupedFilters: Record<string, string[]>,
    facets: search.SkFacetSet[],
    isTitleVisible: boolean = true,
    isEdit: boolean = false
): SavePreferenceConfig => ({
    modalId: isEdit ? 'edit-filter' : 'save-filter',
    title: isEdit ? 'filter.savedFilter.edit.title' : 'filter.savedFilter.modal.title',
    namePlaceholder: 'filter.savedFilter.placeholder.name',
    descriptionPlaceholder: 'filter.savedFilter.placeholder.description',
    notificationToken: isEdit ? 'filter.savedFilter.edit.notification' : undefined,
    errorMessage: isEdit ? 'filter.savedFilter.update.failed' : 'error.savedFilter.saveFailed',
    renderDataPreview: RenderFilterPreview(groupedFilters, (id) => toLabel(id, facets), isTitleVisible)
});
